"use client"

import { clx } from "@/lib/utils"
import { useTranslations, useLocale } from "next-intl"
import { Link } from "@i18n/navigation"
import { ChatBubbleLeftRight } from "@medusajs/icons"

import type { Conversation } from "@lib/data/conversations"

type Props = {
  conversations: Conversation[]
  onSelect?: (id: string) => void
  selectedId?: string | null
}

const formatTime = (iso: string | null, locale: string, yesterdayLabel: string) => {
  if (!iso) return ""
  try {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffDays = Math.floor(diffMs / 86_400_000)

    if (diffDays === 0) {
      return d.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      })
    }
    if (diffDays === 1) {
      return yesterdayLabel
    }
    if (diffDays < 7) {
      return d.toLocaleDateString(locale, { weekday: "short" })
    }
    return d.toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
    })
  } catch {
    return ""
  }
}

const ConversationCard = ({
  conv,
  onSelect,
  selected,
}: {
  conv: Conversation
  onSelect?: (id: string) => void
  selected?: boolean
}) => {
  const t = useTranslations("AccountMessages")
  const locale = useLocale()

  // Title: who you are chatting with. Prefer the seller name, fall back to
  // the subject or the order number without any prefix.
  const name =
    conv.seller_name ??
    conv.subject ??
    conv.order_display_id ??
    ""

  // Preview line: the last message, prefixed with "Вы:" for own messages
  // like a messenger. Falls back to the subject when the title is the
  // seller name, or stays empty when the title already is the subject.
  const preview = conv.last_message
    ? conv.last_message.author_actor_type === "customer"
      ? `${t("you")}: ${conv.last_message.body}`
      : conv.last_message.body
    : (conv.seller_name && conv.subject) ||
      (!conv.seller_name && conv.order_display_id)
      ? conv.subject
      : ""

  const content = (
    <>
      {/* Avatar */}
      <div
        className={clx(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-medium uppercase",
          conv.unread_count > 0
            ? "bg-ui-fg-interactive text-white"
            : "bg-ui-bg-subtle text-ui-fg-subtle"
        )}
        aria-hidden="true"
      >
        {name.charAt(0)}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="txt-compact-small-plus truncate font-medium text-ui-fg-base">
            {name}
          </span>
          {conv.last_message_at && (
            <span className="txt-xsmall whitespace-nowrap text-ui-fg-subtle">
              {formatTime(
                conv.last_message_at,
                locale,
                t("yesterday")
              )}
            </span>
          )}
        </div>

        {preview && (
          <p
            className={clx(
              "mt-1 truncate txt-small",
              conv.unread_count > 0 ? "text-ui-fg-base" : "text-ui-fg-subtle"
            )}
          >
            {preview}
          </p>
        )}
      </div>

      {/* Unread badge */}
      {conv.unread_count > 0 && (
        <span
          className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-ui-fg-interactive px-1.5 txt-xsmall text-white"
          data-testid="unread-badge"
        >
          {conv.unread_count}
        </span>
      )}
    </>
  )

  const className = clx(
    "flex items-start gap-3 rounded-lg p-4 text-left transition-fg hover:bg-ui-bg-subtle",
    selected && "bg-ui-bg-subtle",
    conv.unread_count > 0 && !selected && "border-l-2 border-l-ui-fg-interactive"
  )

  if (onSelect) {
    return (
      <button
        type="button"
        onClick={() => onSelect(conv.id)}
        className={clx(className, "w-full")}
        data-testid="conversation-card"
        data-id={conv.id}
      >
        {content}
      </button>
    )
  }

  return (
    <Link href={`/account/messages/${conv.id}`} className="block w-full">
      <div
        className={clx(
          "flex items-start gap-3 rounded-lg border border-ui-border-base p-4 transition-fg hover:bg-ui-bg-subtle",
          conv.unread_count > 0 && "border-l-2 border-l-ui-fg-interactive"
        )}
        data-testid="conversation-card"
        data-id={conv.id}
      >
        {content}
      </div>
    </Link>
  )
}

const ConversationList = ({
  conversations,
  onSelect,
  selectedId,
}: Props) => {
  const t = useTranslations("AccountMessages")

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-ui-bg-subtle">
          <ChatBubbleLeftRight width={24} height={24} className="text-ui-fg-subtle" />
        </div>
        <p className="txt-small text-ui-fg-subtle">{t("emptyStateDescription")}</p>
      </div>
    )
  }

  return (
    <ul className="flex flex-col gap-1">
      {conversations.map((conv) => (
        <li key={conv.id} className="w-full">
          <ConversationCard
            conv={conv}
            onSelect={onSelect}
            selected={selectedId === conv.id}
          />
        </li>
      ))}
    </ul>
  )
}

export default ConversationList
